import {
  CorporationType,
  Country,
  CredentialBlockType,
  RegistrationSource,
  RegistrationStatus,
  RegistrationValidationStatus,
} from '@prisma/client';
import { IGovernmentIdentifier } from '../government-identifiers';

export enum RegistrationPurpose {
  INCOME_TAX = 'INCOME_TAX',
  INDIRECT_TAX = 'INDIRECT_TAX',
  PAYROLL = 'PAYROLL',
  CORPORATE_REGISTRY = 'CORPORATE_REGISTRY',
  SOCIAL_SECURITY = 'SOCIAL_SECURITY',
  TRADE = 'TRADE',
  OTHER = 'OTHER',
  FORMATION = 'FORMATION',
  REGULATORY_REPORTING = 'REGULATORY_REPORTING',
  TAX_AND_COMPLIANCE_REPORTING = 'TAX_AND_COMPLIANCE_REPORTING',
}

export enum RegistrationApplicability {
  BOTH = 'BOTH',
  DOMESTIC = 'DOMESTIC',
  FOREIGN = 'FOREIGN',
}

/** What a view asks for. Each value selects exactly that applicability, so the lists never overlap. */
export enum RegistrationApplicabilityFilter {
  BOTH = 'BOTH',
  DOMESTIC = 'DOMESTIC',
  FOREIGN = 'FOREIGN',
  CUSTOM = 'CUSTOM',
}

export type TGetCredentialsForRegistration = {
  credentials: {
    id: string;
    blocks: {
      id: string;
      key: string;
      mask: string;
      type: CredentialBlockType;
    }[];
  }[];
};

export interface IRegistration {
  id: string | null; // If the id is not present, then the identifier doesn't exist yet.

  country: Country; // ISO 3166 2 alpha country code
  /** Where it is registered, as the content names it: ADM1_US-DE, or ADM0_US for the country itself. */
  jurisdictionId: string | null;

  businessEntityId: number;
  corporationType: CorporationType;

  effectiveStartDate: string;
  effectiveEndDate: string | null;

  registeredBy: RegistrationSource;

  displayName: string;
  description: string;

  purpose: RegistrationPurpose;

  status: RegistrationValidationStatus;
  registrationStatus: RegistrationStatus;

  createdAt: number | null; // EPOCH time stamp
  lastUpdatedAt: number | null; // EPOCH time stamp

  credentials?:
    | {
        id: string;
        blocks: {
          id: string;
          key: string;
          mask: string;
          type: CredentialBlockType;
        }[];
      }[]
    | null;

  /** The content registration id, e.g. REG:US_DE:LLC_FORMATION. Empty for a custom registration. */
  registrationIdentifier: string;

  /** Whether it is for a domestic entity, a foreign one, or both. Null for a custom registration. */
  applicability: RegistrationApplicability | null;

  isCustom: boolean;

  governmentIdentifiers?: IGovernmentIdentifier[] | null;

  /** What this registration produces and the entity has not recorded yet, in the same shape as a held one. */
  unheldGovernmentIdentifiers?: IGovernmentIdentifier[];

  /** Filed with the authority, or issued by it. */
  documents?: RegistrationDocument[];

  hasLinkedCredential?: boolean;
  linkedCredential?: TGetCredentialsForRegistration['credentials'][number];
}

export type TSupportedRegistration = {
  displayName: string;
  description: string;
  purpose: RegistrationPurpose;
  /** As the content spells it, e.g. ADM1_US-DE. */
  jurisdictionId: string;
  registrationIdentifier: string;
  corporationType: CorporationType;
  country: Country;
  applicability: RegistrationApplicability;
  /** What this registration produces, and what each one is called, so a form can label the field asking for it. */
  producedGovernmentIdentifiers?: TProducedGovernmentIdentifier[];
};

export type TProducedGovernmentIdentifier = {
  identifierTypeId: string | null;
  displayName: string;
  /** A realistic value, for the field's placeholder. */
  example: string | null;
  /** What a value has to match, where anything says so. Null leaves the field unvalidated. */
  regex: string | null;
};

export type CreateRegistrationProps = {
  country: Country;
  registrationIdentifier?: string;
  name?: string;
  registrationPurpose?: RegistrationPurpose;
  status: RegistrationValidationStatus;
  jurisdiction?: string | null;
  effectiveStartDate?: string;
  effectiveEndDate?: string;
  // Optional: identifier-less registration rules create a registration with
  // no linked government identifier.
  /**
   * One entry per identifier the registration produces, each naming the type it carries a value for.
   * An entry may leave the type out where the registration produces one, and a custom registration's
   * always does: its number is held under an id the backend mints from its name.
   */
  governmentIdentifiers?: {
    identifierTypeId?: string;
    value: string;
  }[];
};

export enum RegistrationDocumentRole {
  FILED = 'FILED',
  PRODUCED = 'PRODUCED',
}

export type RegistrationDocument = {
  fileId: number;
  fileName: string;
  fileUrl: string | null;
  role: RegistrationDocumentRole;
  /** The content type this file is an instance of, e.g. DOC:US:CP575. */
  documentTypeId: string | null;
};

export type RegistrationIdentifierUpdate = {
  id: string;
  value: string;
};

export type UpdateRegistrationProps = {
  status: RegistrationValidationStatus;
  effectiveStartDate?: string;
  effectiveEndDate?: string;
  governmentIdentifiers?: RegistrationIdentifierUpdate[];
};

export enum ListRegistrationsExtensibleFields {
  CREDENTIALS = 'CREDENTIALS',
}

export class ListRegistrationsQueryParams {
  extend?: ListRegistrationsExtensibleFields[];
}

export enum GetRegistrationExtendsOptions {
  CREDENTIALS = 'CREDENTIALS',
}
